import { AlertTriangle, RotateCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Jaring pengaman terakhir untuk error render.
 *
 * Tanpa ini, satu error yang tak tertangkap membuat React melepas
 * seluruh pohon komponen dan pengguna hanya melihat halaman putih
 * kosong — tanpa pesan, tanpa cara memulihkan.
 *
 * Kasus paling sering di produksi: pengguna membuka aplikasi, lalu versi
 * baru di-deploy dan mengganti berkas chunk ber-hash. Saat pengguna
 * berpindah halaman, chunk lazy yang lama sudah tidak ada di server dan
 * `import()` gagal. Situasi ini pulih hanya dengan memuat ulang halaman,
 * jadi itulah yang ditawarkan.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // TODO: kirim ke layanan pemantauan error saat sudah tersedia.
    console.error('Error tidak tertangani:', error, info.componentStack);
  }

  /** Mendeteksi kegagalan memuat chunk, yang butuh pesan berbeda. */
  private isChunkLoadError(error: Error): boolean {
    return (
      /Failed to fetch dynamically imported module/i.test(error.message) ||
      /Importing a module script failed/i.test(error.message) ||
      /ChunkLoadError/i.test(error.name)
    );
  }

  override render(): ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    const isStale = this.isChunkLoadError(error);

    return (
      <div className="flex min-h-dvh items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div
            className="mx-auto flex size-12 items-center justify-center rounded-full bg-[var(--color-destructive-subtle)]"
            aria-hidden="true"
          >
            <AlertTriangle className="size-6 text-[var(--color-destructive)]" />
          </div>

          <h1 className="mt-5 text-xl font-semibold tracking-tight">
            {isStale ? 'Versi aplikasi telah diperbarui' : 'Terjadi kesalahan'}
          </h1>

          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            {isStale
              ? 'Ada versi baru SIPAUD. Muat ulang halaman untuk melanjutkan.'
              : 'Maaf, terjadi kesalahan yang tidak terduga. Muat ulang halaman untuk mencoba lagi.'}
          </p>

          <Button className="mt-6" onClick={() => window.location.reload()}>
            <RotateCw aria-hidden="true" />
            Muat Ulang Halaman
          </Button>

          {/* Detail teknis hanya saat development — di produksi pesan
              error bisa membocorkan struktur internal aplikasi. */}
          {import.meta.env.DEV && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-xs text-[var(--color-muted-foreground)]">
                Detail teknis
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto rounded-[var(--radius-sm)] bg-[var(--color-muted)] p-3 text-left text-xs">
                {error.message}
                {'\n\n'}
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
